/******/ (() => { // webpackBootstrap
var __webpack_exports__ = {};
const featuredCollectionFocusableSelector = [
  'a[href]',
  'button',
  'input',
  'select',
  'textarea',
  'summary',
  'iframe',
  'audio[controls]',
  'video[controls]',
  '[contenteditable]:not([contenteditable="false"])',
  '[tabindex]',
].join(',');
const featuredCollectionVisibilityTolerance = 2;
let featuredCollectionControllerId = 0;

function requestFeaturedCollectionFrame(callback) {
  if (window.requestAnimationFrame) {
    return window.requestAnimationFrame(callback);
  }

  return window.setTimeout(callback, 16);
}

function cancelFeaturedCollectionFrame(frameId) {
  if (window.cancelAnimationFrame) {
    window.cancelAnimationFrame(frameId);
    return;
  }

  window.clearTimeout(frameId);
}

function setFeaturedCollectionAttribute(element, name, value) {
  if (element.getAttribute(name) !== value) {
    element.setAttribute(name, value);
  }
}

function restoreFeaturedCollectionTabState(target, state) {
  if (state.hadTabindex) {
    setFeaturedCollectionAttribute(target, 'tabindex', state.tabindex);
  } else if (target.hasAttribute('tabindex')) {
    target.removeAttribute('tabindex');
  }

  state.suppressed = false;
}

function getFeaturedCollectionFocusTargets(cell) {
  const targets = [];

  if (cell.matches(featuredCollectionFocusableSelector)) {
    targets.push(cell);
  }

  return targets.concat(Array.from(cell.querySelectorAll(featuredCollectionFocusableSelector)));
}

function isFeaturedCollectionCellVisible(cellRect, viewportRect) {
  return cellRect.width > 0 &&
    cellRect.height > 0 &&
    cellRect.left >= viewportRect.left - featuredCollectionVisibilityTolerance &&
    cellRect.top >= viewportRect.top - featuredCollectionVisibilityTolerance &&
    cellRect.right <= viewportRect.right + featuredCollectionVisibilityTolerance &&
    cellRect.bottom <= viewportRect.bottom + featuredCollectionVisibilityTolerance;
}

function createFeaturedCollectionA11yController($slider, finiteCollection) {
  const sliderElement = $slider[0];
  const namespace = `.pxuFeaturedCollectionA11y${++featuredCollectionControllerId}`;
  const tabStates = new Map();
  const expectedHiddenStates = new WeakMap();

  const controller = {
    namespace: namespace,
    frameId: null,
    focusedCell: null,
    mutationObserver: null,
    resizeObserver: null,
    cellResizeObserver: null,
    observedCells: new Set(),
    cellSizes: new Map(),
    layoutDirty: Boolean(finiteCollection),
    viewport: null,
    destroyed: false,
    endpointFocusHooks: [],

    schedule: function() {
      if (this.destroyed || this.frameId !== null) {
        return;
      }

      this.frameId = requestFeaturedCollectionFrame(() => {
        this.frameId = null;
        this.sync();
      });
    },

    connectEndpointFocus: function() {
      if (!finiteCollection || this.destroyed || this.endpointFocusHooks.length) {
        return;
      }

      const flickity = $slider.data('flickity');
      if (!flickity || !flickity.prevButton || !flickity.nextButton) {
        return;
      }

      [flickity.prevButton, flickity.nextButton].forEach((button) => {
        const originalDisable = button.disable;
        const hadOwnDisable = Object.prototype.hasOwnProperty.call(button, 'disable');
        // Vendor select listeners disable arrows before the jQuery select event.
        // Keep this hook on the owned instance, never the vendor prototype.
        const disable = function() {
          if (this === button && !controller.destroyed &&
            sliderElement.__pxuFeaturedCollectionA11yController === controller &&
            $slider.data('flickity') === flickity && flickity.isActive &&
            !flickity.options.wrapAround && button.isEnabled && !button.element.disabled &&
            document.activeElement === button.element && sliderElement.isConnected &&
            sliderElement.getClientRects().length) {
            const other = button === flickity.prevButton ? flickity.nextButton : flickity.prevButton;
            let target = sliderElement;

            if (flickity.slides.length > 1 && flickity.selectedIndex !==
              (other.isPrevious ? 0 : flickity.slides.length - 1)) {
              // Previous updates first; Next may still be disabled from the far end.
              other.update();
              if (other.isEnabled && !other.element.disabled) {
                target = other.element;
              }
            }

            if (target.isConnected && target.getClientRects().length &&
              (target !== sliderElement || target.hasAttribute('tabindex'))) {
              target.focus({ preventScroll: true });
            }
          }

          return originalDisable.apply(this, arguments);
        };

        button.disable = disable;
        this.endpointFocusHooks.push({ button, disable, originalDisable, hadOwnDisable });
      });
    },

    connectObservers: function() {
      if (this.destroyed) {
        return;
      }

      const nextViewport = sliderElement.querySelector('.flickity-viewport');

      if (nextViewport !== this.viewport) {
        if (this.resizeObserver) {
          this.resizeObserver.disconnect();
          this.resizeObserver = null;
        }

        this.viewport = nextViewport;

        if (this.viewport && window.ResizeObserver) {
          this.resizeObserver = new window.ResizeObserver(() => this.schedule());
          this.resizeObserver.observe(this.viewport);
        }
      }

      if (!this.mutationObserver && window.MutationObserver) {
        this.mutationObserver = new window.MutationObserver((records) => {
          const instanceRemoved = records.some((record) => record.type === 'childList') &&
            (!$slider.data('flickity') || !this.viewport || !sliderElement.contains(this.viewport));

          if (instanceRemoved) {
            this.cleanup();
            return;
          }

          const hasUnexpectedState = records.some((record) => {
            if (record.type === 'childList') {
              return true;
            }

            const expectedState = expectedHiddenStates.get(record.target);
            return expectedState === undefined ||
              record.target.getAttribute('aria-hidden') !== expectedState;
          });

          if (hasUnexpectedState) {
            this.schedule();
          }
        });
        this.mutationObserver.observe(sliderElement, {
          attributes: true,
          attributeFilter: ['aria-hidden'],
          childList: true,
          subtree: true,
        });
      }
    },

    observeCellSizes: function(cells) {
      if (!finiteCollection || this.destroyed || !window.ResizeObserver) {
        return;
      }

      if (!this.cellResizeObserver) {
        this.cellResizeObserver = new window.ResizeObserver((entries) => {
          if (this.destroyed) {
            return;
          }

          let changed = false;
          entries.forEach((entry) => {
            if (!this.observedCells.has(entry.target)) {
              return;
            }

            const rect = entry.target.getBoundingClientRect();
            const previous = this.cellSizes.get(entry.target);
            if (!previous || Math.abs(rect.width - previous.width) > 0.5 ||
              Math.abs(rect.height - previous.height) > 0.5) {
              this.cellSizes.set(entry.target, { width: rect.width, height: rect.height });
              changed = true;
            }
          });

          if (changed) {
            this.layoutDirty = true;
            this.schedule();
          }
        });
      }

      const currentCells = new Set(cells);
      this.observedCells.forEach((cell) => {
        if (!currentCells.has(cell)) {
          this.cellResizeObserver.unobserve(cell);
          this.observedCells.delete(cell);
          this.cellSizes.delete(cell);
          this.layoutDirty = true;
        }
      });
      cells.forEach((cell) => {
        if (!this.observedCells.has(cell)) {
          const rect = cell.getBoundingClientRect();
          this.observedCells.add(cell);
          this.cellSizes.set(cell, { width: rect.width, height: rect.height });
          this.cellResizeObserver.observe(cell);
          this.layoutDirty = true;
        }
      });
    },

    setTargetSuppressed: function(target, shouldSuppress) {
      let state = tabStates.get(target);

      if (shouldSuppress) {
        if (!state || !state.suppressed) {
          state = {
            hadTabindex: target.hasAttribute('tabindex'),
            tabindex: target.getAttribute('tabindex'),
            suppressed: true,
          };
          tabStates.set(target, state);
        }

        setFeaturedCollectionAttribute(target, 'tabindex', '-1');
      } else if (state && state.suppressed) {
        restoreFeaturedCollectionTabState(target, state);
      }
    },

    setCellVisible: function(measurement, isVisible) {
      const hiddenState = isVisible ? 'false' : 'true';
      expectedHiddenStates.set(measurement.cell, hiddenState);
      setFeaturedCollectionAttribute(measurement.cell, 'aria-hidden', hiddenState);

      measurement.targets.forEach((target) => {
        this.setTargetSuppressed(target, !isVisible);
      });
    },

    sync: function() {
      if (this.destroyed) {
        return;
      }

      this.connectObservers();

      const flickity = $slider.data('flickity');
      const viewport = this.viewport;

      if (!flickity || !viewport || typeof flickity.getCellElements !== 'function') {
        return;
      }

      const cells = Array.from(flickity.getCellElements());
      this.observeCellSizes(cells);

      if (finiteCollection && this.layoutDirty && flickity.isActive &&
        !flickity.isAnimating && !flickity.isDragging &&
        typeof flickity.resize === 'function' && viewport.getBoundingClientRect().width > 0) {
        // Clear first: resize can emit select and schedule another visibility sync.
        this.layoutDirty = false;
        flickity.resize();
      }

      const viewportRect = viewport.getBoundingClientRect();
      const activeElement = document.activeElement;
      const measurements = cells.map((cell) => {
        const rect = cell.getBoundingClientRect();
        return {
          cell: cell,
          targets: getFeaturedCollectionFocusTargets(cell),
          containsFocus: Boolean(activeElement && cell.contains(activeElement)),
          visible: isFeaturedCollectionCellVisible(rect, viewportRect),
        };
      });
      const focusedOffscreenCell = measurements.find((measurement) => {
        return measurement.containsFocus && !measurement.visible;
      });

      if (focusedOffscreenCell && this.focusedCell !== focusedOffscreenCell.cell &&
        typeof flickity.selectCell === 'function') {
        this.setCellVisible(focusedOffscreenCell, true);
        this.focusedCell = focusedOffscreenCell.cell;
        flickity.selectCell(focusedOffscreenCell.cell, true, true);
        this.schedule();
        return;
      }

      if (!focusedOffscreenCell) {
        this.focusedCell = null;
      }

      measurements.forEach((measurement) => {
        this.setCellVisible(
          measurement,
          measurement.visible || measurement.containsFocus
        );
      });
    },

    cleanup: function() {
      if (this.destroyed) {
        return;
      }

      this.destroyed = true;

      this.endpointFocusHooks.forEach((hook) => {
        if (hook.button.disable === hook.disable) {
          if (hook.hadOwnDisable) {
            hook.button.disable = hook.originalDisable;
          } else {
            delete hook.button.disable;
          }
        }
      });
      this.endpointFocusHooks.length = 0;

      if (this.frameId !== null) {
        cancelFeaturedCollectionFrame(this.frameId);
        this.frameId = null;
      }

      if (this.mutationObserver) {
        this.mutationObserver.disconnect();
        this.mutationObserver = null;
      }

      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
        this.resizeObserver = null;
      }

      if (this.cellResizeObserver) {
        this.cellResizeObserver.disconnect();
        this.cellResizeObserver = null;
      }
      this.observedCells.clear();
      this.cellSizes.clear();
      this.layoutDirty = false;

      $slider.off(namespace);
      $(window).off(namespace);

      tabStates.forEach((state, target) => {
        if (state.suppressed) {
          restoreFeaturedCollectionTabState(target, state);
        }
      });
      tabStates.clear();

      if (sliderElement.__pxuFeaturedCollectionA11yController === this) {
        delete sliderElement.__pxuFeaturedCollectionA11yController;
      }
    },
  };

  sliderElement.__pxuFeaturedCollectionA11yController = controller;

  if (!window.ResizeObserver) {
    $(window).on(`resize${namespace}`, () => {
      if (finiteCollection) {
        controller.layoutDirty = true;
      }
      controller.schedule();
    });
  }

  return controller;
}

window.PXUTheme.jsFeaturedCollection = {
  init: function($section) {
    // Add settings from schema to current object
    window.PXUTheme.jsFeaturedCollection = $.extend(this, window.PXUTheme.getSectionData($section));

    if (this.collection_layout == 'slider') {
      this.createSlider($section.find('.slider-gallery--desktop'));
    }

    if (this.collection_layout_mobile == 'slider') {
      this.createSlider($section.find('.slider-gallery--mobile'));
    }
  },
  createSlider: function($sliders) {
    $sliders.each((index, sliderElement) => {
      const $slider = $(sliderElement);
      const finiteCollection = sliderElement.closest('.msg-featured-collection--finite') !== null;
      const existingController = sliderElement.__pxuFeaturedCollectionA11yController;

      if (existingController) {
        existingController.cleanup();
      }

      const controller = createFeaturedCollectionA11yController($slider, finiteCollection);
      const scheduleSync = (event) => {
        if (finiteCollection && event && (event.type === 'ready' || event.type === 'lazyLoad')) {
          controller.layoutDirty = true;
        }
        controller.schedule();
      };
      const namespace = controller.namespace;

      $slider.on([
        `ready.flickity${namespace}`,
        `select.flickity${namespace}`,
        `scroll.flickity${namespace}`,
        `lazyLoad.flickity${namespace}`,
      ].join(' '), scheduleSync);
      $slider.on(`settle.flickity${namespace}`, () => {
        if (!finiteCollection) {
          $slider.flickity('resize');
        } else if (!window.ResizeObserver) {
          controller.layoutDirty = true;
        }
        controller.schedule();
      });
      $slider.on(`focusin${namespace} focusout${namespace}`, scheduleSync);

      if (!$slider.data('flickity')) {
        $slider.flickity({
          lazyLoad: 2,
          freeScroll: true,
          imagesLoaded: true,
          draggable: true,
          cellAlign: 'center',
          wrapAround: !finiteCollection,
          pageDots: false,
          contain: true,
          prevNextButtons: this.products_limit > this.products_per ? true : false,
          initialIndex: 0,
          arrowShape: arrowShape,
        });
      }

      controller.connectEndpointFocus();
      controller.connectObservers();
      controller.schedule();
    });
  },
  unload: function($section) {
    const $sliders = $section.find('.slider-gallery--desktop, .slider-gallery--mobile');

    $sliders.each((index, sliderElement) => {
      const $slider = $(sliderElement);
      const controller = sliderElement.__pxuFeaturedCollectionA11yController;

      if (controller) {
        controller.cleanup();
      }

      if ($slider.data('flickity')) {
        $slider.flickity('destroy');
      }
    });
  }
}

/******/ })()
;
